import { Control, Controller, FieldValues, Path, PathValue, useWatch } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useState, useRef } from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CustomInput } from "./input";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";

interface ControlledInputV2Props<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  defaultValue?: PathValue<T, Path<T>>;
  label?: string;
  placeholder?: string;
  type?: React.HTMLInputTypeAttribute;
  disabled?: boolean;
  className?: string;
  showSuggestion?: boolean;
  rightElement?: React.ReactNode;
  tooltip?: string;
  disableDecimal?: boolean;
  maxDecimal?: number;
  inputType?: "text" | "number" | "float";
}

export const ControlledInputV3 = <T extends FieldValues>({
  control,
  name,
  defaultValue,
  label,
  placeholder,
  type = "text",
  disabled = false,
  className,
  showSuggestion,
  rightElement,
  tooltip,
  disableDecimal = false,
  maxDecimal = 2,
  inputType = "text",
}: ControlledInputV2Props<T>) => {
  const translateMessage = useTranslateMessage();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const valueWatch = useWatch({ control, name });

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field, fieldState: { error } }) => (
        <div className={cn("flex flex-col gap-2", className)}>
          <div
            className={cn(
              "relative flex h-14 w-full items-center rounded-lg border bg-white px-4 transition-all duration-200",
              {
                "border-blue-600": isFocused,
                "border-red-500": error,
                "border-gray-300": !isFocused && !error,
                "bg-gray-50": disabled,
                "pointer-events-none": disabled,
                "hover:border-gray-400": !disabled && !isFocused && !error,
              },
            )}
            onClick={() => {
              if (!disabled) {
                setIsFocused(true);
                inputRef.current?.focus();
              }
            }}
          >
            {label && (
              <Label
                className={cn(
                  "pointer-events-none absolute text-sm text-gray-500 transition-all duration-200 ease-in-out",
                  {
                    "-translate-y-3 text-xs":
                      (!!valueWatch || valueWatch === 0) && valueWatch.toString().length > 0,
                    "hidden translate-y-0 text-sm":
                      (valueWatch !== 0 && !valueWatch) || valueWatch.toString().length === 0,
                  },
                )}
                htmlFor={name}
              >
                {label}
              </Label>
            )}
            <CustomInput
              {...field}
              inputType={inputType}
              maxDecimals={maxDecimal}
              ref={(e) => {
                field.ref(e);
                if (inputRef.current !== e) {
                  (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = e;
                }
              }}
              id={name}
              onWheel={(e) => {
                if (type === "number") {
                  e.currentTarget.blur();
                }
              }}
              onKeyDown={(e) => {
                if (disableDecimal && type === "number" && (e.key === "." || e.key === ",")) {
                  e.preventDefault();
                }
              }}
              disabled={disabled}
              placeholder={placeholder}
              autoComplete={showSuggestion ? undefined : "off"}
              className={cn(
                "peer flex h-[80%] w-full border-0 border-transparent bg-transparent p-0 shadow-none focus:border-transparent focus:outline-none focus-visible:ring-0",
                {
                  "translate-y-1.5":
                    label && (valueWatch || valueWatch === 0) && valueWatch.toString().length > 0,
                },
              )}
              onFocus={() => {
                setIsFocused(true);
              }}
              onBlur={() => {
                setIsFocused(false);
              }}
              value={field.value ?? ""}
            />

            {rightElement && <div className="ml-2 flex flex-row items-center">{rightElement}</div>}

            {tooltip && (
              <div className="ml-2 self-center">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="size-5 cursor-help text-blue-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-64 text-sm">{tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>

          {error && <p className="text-xs text-red-500">{translateMessage(error.message)}</p>}
        </div>
      )}
    />
  );
};
